<template>
  <div>
    <vue-headful :title="this.setPageTitle('efficiencyTestCreate')" />
    <loading :active="getLoading" :color="'blue'" is-full-page loader="spinner"> </loading>

    <b-container class="my-4">
      <!-- HEADER -->
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <h1 class="mb-0 h4 font-weight-normal">
            {{ $t('efficiencyTestDetails.title') }}
          </h1>
        </template>
        <template v-slot:right-content>
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="cancelEditingProfile">
            {{ $t('common.cancel') }}
          </b-button>
          <b-button size="sm" variant="primary" :disabled="!canSave" @click="saveScoringProfile()">
            {{ $t('common.save') }}
          </b-button>
        </template>
      </custom-flex-header>

      <!-- NAME + DESCRIPTION -->
      <b-row class="mb-4">
        <b-col>
          <div class="d-flex">
            <div class="mr-2 w-50">
              <span>{{ $t('common.name') }}</span>
              <b-input
                size="sm"
                type="text"
                autocomplete="off"
                :formatter="nameFormatter"
                v-model="scoringProfileName"
                :placeholder="$t('efficiencyTestDetails.name')"
              ></b-input>
            </div>
            <div class="w-50">
              <span>{{ $t('common.description') }}</span>
              <b-input
                size="sm"
                type="text"
                autocomplete="off"
                :formatter="descriptionFormatter"
                v-model="scoringProfileDescription"
                :placeholder="$t('efficiencyTestDetails.description')"
              ></b-input>
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col>
          <div class="product-test-color mb-3">
            {{ $t('efficiencyTestDetails.productUnderTesting') }}
          </div>
          <div class="border-rounded bg-white p-3">
            <div class="product-test-color" style="color: #212529">
              {{ selectedProduct ? selectedProduct.name : $t('common.notAvailable') }}
            </div>
            <div class="d-flex align-items-center flex-wrap mt-3 product-test-color">
              <span class="font-weight-bold">{{ $t('productDetails.fields.Characteristics.isin') }}:</span>
              <span class="p-sm-1">{{ productField('isin') }}</span
              ><span class="px-1">-</span>
              <span class="font-weight-bold">{{ $t('productDetails.fields.Characteristics.acepiCategory') }}:</span>
              <span class="p-sm-1"> {{ productField('acepiCategory') }}</span
              ><span class="px-1">-</span>
              <span class="font-weight-bold">{{ $t('productDetails.fields.Characteristics.maturityDate') }}:</span>
              <span class="p-sm-1">{{ productField('maturityDate') }}</span
              ><span class="px-1">-</span>
              <span class="font-weight-bold">{{ $t('productDetails.fields.Characteristics.currency') }}:</span>
              <span class="p-sm-1">{{ productField('currency') }}</span>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- TABS -->
      <b-row class="mb-4">
        <b-col>
          <b-tabs
            v-model="currentTab"
            class="fairmat-primary-tabs"
            @activate-tab="onActivateTab"
            data-test="tabsScoringDefinition"
          >
            <b-tab :title="$t('efficiencyTestDetails.comparisonCriteria.title')">
              <comparison-criteria ref="comparisonCriteria"></comparison-criteria>
            </b-tab>
            <b-tab :title="$t('efficiencyTestDetails.profileCoverageDetail')">
              <tab-profile-coverage ref="tabProfileCoverage"></tab-profile-coverage>
            </b-tab>
            <b-tab :title="$t('efficiencyTestDetails.scoringDefinition')">
              <tab-scoring-definition></tab-scoring-definition>
            </b-tab>
          </b-tabs>

          <div class="d-flex align-items-center justify-content-end mt-3">
            <span v-if="!canSave" class="small text-muted mx-3">{{
              $t('efficiencyTestDetails.saveRequirements.intro', { missing: saveMissing.join(', ') })
            }}</span
            ><button
              :disabled="!canSave"
              class="btn btn-sm btn-primary"
              @click="saveScoringProfile({ execute: true })"
            >
              <i class="fas fa-sync-alt"></i>
              {{ $t('efficiencyTestDetails.saveAndRun') }}
            </button>
          </div>
          <div class="text-right text-muted small mt-3">
            {{ $t('efficiencyTestDetails.saveAndRunDuration') }}
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
// Same 3-tab shell as the legacy EfficiencyTestsCreate.vue, with the first tab now showing the
// comparison-criteria panel (mock/CreazioneTest.vue) instead of the catalog-driven filters panel.
// TabProfileCoverage/TabScoringDefinition are reused unchanged.
import TabProfileCoverage from './TabProfileCoverage.vue'
import TabScoringDefinition from './TabScoringDefinition.vue'
import CustomFlexHeader from '../../components/Lib/CustomFlexHeader.vue'
import ProfileCoverage from '../../components/EfficiencyTests/ProfileCoverage.vue'
import ComparisonCriteria from '../../components/EfficiencyTests/ComparisonCriteria.vue'
import { detailFieldDisplayValue } from '../../utils/detailFieldFormat'
import unsavedChangesMixin from '../../mixins/unsavedChanges'
import { efficiencyTestSnapshot } from '../../utils/unsavedChangesSnapshots'
import efficiencyTestSaveRulesMixin from '../../mixins/efficiencyTestSaveRules'

export default {
  mixins: [unsavedChangesMixin, efficiencyTestSaveRulesMixin],

  components: {
    CustomFlexHeader,
    TabProfileCoverage,
    TabScoringDefinition,
    ProfileCoverage,
    ComparisonCriteria,
  },

  data() {
    return {
      currentTab: 0,
      maxProfileNameLength: 128,
      maxProfileDescriptionLength: 255,
    }
  },

  computed: {
    getLoading: {
      get() {
        return this.$store.getters['efficiencyTestsCommon/getLoading']
      },
    },
    scoringProfileName: {
      get() {
        return this.$store.getters['efficiencyTestsCommon/getScoringProfileName']
      },
      set(newVal) {
        this.$store.dispatch('efficiencyTestsCommon/setScoringProfileName', this.nameFormatter(newVal))
      },
    },
    scoringProfileDescription: {
      get() {
        return this.$store.getters['efficiencyTestsCommon/getScoringProfileDescription']
      },
      set(newVal) {
        this.$store.dispatch('efficiencyTestsCommon/setScoringProfileDescription', this.descriptionFormatter(newVal))
      },
    },
    getProfileCoverage() {
      return this.$store.getters['efficiencyTestsCommon/getProfileCoverage']
    },
    selectedProduct() {
      return this.$store.getters['efficiencyTestsCommon/getSelectedProduct']
    },
  },

  methods: {
    unsavedChangesSnapshot() {
      return efficiencyTestSnapshot(this)
    },

    unsavedChangesReady() {
      return !this.getLoading
    },

    // Looks up `key` in the product's Characteristics sidebar fields (see
    // /api/detail-page/product's sidebar[0].fields) and formats it exactly like the product detail
    // page does — dates via $d, Enum values via the searchNew.options.* labels, "N/D" when missing.
    productField(key) {
      const characteristics = (this.selectedProduct && this.selectedProduct.characteristics) || []
      const field = characteristics.find((f) => f.key === key)
      if (!field) {
        return this.$t('common.notAvailable')
      }
      return detailFieldDisplayValue(this, field).text
    },

    nameFormatter(value) {
      return value.substring(0, this.maxProfileNameLength)
    },
    descriptionFormatter(value) {
      return value.substring(0, this.maxProfileDescriptionLength)
    },
    saveScoringProfile({ execute = false } = {}) {
      const isValidName = this.$store.getters['efficiencyTestsCommon/getScoringProfileNameValidation']

      if (!isValidName) {
        this.$bvToast.toast(this.$t('efficiencyTests.errorOnMissingName'), {
          title: this.$t('efficiencyTests.scoringDefinition'),
          variant: 'danger',
        })
        return
      }

      const results = this.$store.getters['efficiencyTestsCommon/getScoringProfileDefinitionValidation']
      const modelId = this.$store.getters['efficiencyTestsDefinition/getSelectedModelId']
      const driverFamiliesList = this.$store.getters['efficiencyTestsDefinition/getUnsortedModelDrivers']
      const sortedDrivers = this.$store.getters['efficiencyTestsDefinition/getSortedModelDrivers']

      // if model selected NO need to validate scoring definition
      if (!modelId) {
        if (!results.isValidScoringDefinition) {
          this.$bvToast.toast(this.$t('efficiencyTests.errorOnDriversSelected'), {
            title: this.$t('efficiencyTests.scoringDefinition'),
            variant: 'danger',
          })
        }

        if (results.isValidScoringDefinition && !results.isValidScoringDefinitionTotalWeight) {
          this.$bvToast.toast(this.$t('efficiencyTests.errorOnTotalWeight'), {
            title: this.$t('efficiencyTests.scoringDefinition'),
            variant: 'danger',
          })
        }

        if (!results.isDriversWeightsGreaterThanZero) {
          this.$bvToast.toast(this.$t('efficiencyTests.weightsMustBeGreaterThan0'), {
            title: this.$t('efficiencyTests.scoringDefinition'),
            variant: 'danger',
          })
        }
      } else {
        if (driverFamiliesList.length !== sortedDrivers.length) {
          this.$bvToast.toast(this.$t('efficiencyTests.allDriversMustBeSelectedAndSorted'), {
            title: this.$t('efficiencyTests.scoringDefinition'),
            variant: 'danger',
          })
        }
      }

      const isValid = modelId
        ? driverFamiliesList.length === sortedDrivers.length
        : Object.values(results).every((j) => j !== false)

      if (isValid) {
        const criteria = this.$refs.comparisonCriteria ? this.$refs.comparisonCriteria.getCriteriaPayload() : []
        this.$store
          .dispatch('efficiencyTestsEditor/saveScoringProfile', criteria)
          .then((data) => {
            const afterSave = execute ? this.executeSavedTest(data && data.id) : Promise.resolve()
            return afterSave.then(() => this.$router.push({ name: 'efficiencyTestsList' }))
          })
          .catch(async (error) => {
            const err = await error.response
            const message = await err.text()
            if (message.includes('NameInUse')) {
              this.$bvToast.toast(this.$t('efficiencyTests.nameInUse'), {
                title: this.$t('efficiencyTests.title'),
                variant: 'danger',
              })
            } else if (message.includes('ScoringDefinitionWeightInvalid')) {
              this.$bvToast.toast(this.$t('efficiencyTests.scoringDefinitionWeightInvalid'), {
                title: this.$t('efficiencyTests.title'),
                variant: 'danger',
              })
            } else {
              this.$bvModal.msgBoxOk(this.$t('common.genericError'))
            }
          })
      }
    },
    // The store already holds the product when coming from the list/product page; on a direct visit
    // or refresh it is reloaded from the productId query. A stale product from a previous visit is dropped.
    loadProductFromQuery() {
      const { productId } = this.$route.query
      const current = this.selectedProduct
      if (current && productId && String(current.productId) === String(productId)) {
        return
      }
      this.$store.dispatch('efficiencyTestsCommon/setSelectedProduct', null)
      if (!productId) {
        return
      }
      this.$store.dispatch('efficiencyTestsCommon/setLoading', true)
      this.$store
        .dispatch('efficiencyTestsCommon/loadSelectedProductById', productId)
        .then((product) => {
          if (!product) {
            this.$bvModal.msgBoxOk(this.$t('common.genericError'))
          }
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
        .finally(() => {
          this.$store.dispatch('efficiencyTestsCommon/setLoading', false)
        })
    },
    cancelEditingProfile() {
      this.$bvModal
        .msgBoxConfirm(this.$t('efficiencyTestsList.modalMessages.confirmUnsaved'), {
          cancelTitle: this.$t('common.no'),
          okTitle: this.$t('common.yes'),
        })
        .then((value) => {
          // OK = value = true
          if (value) {
            this.$router.push({ name: 'efficiencyTestsList' })
          }
        })
    },
    onActivateTab(newTabIndex, prevTabIndex, firedEvent) {
      if (newTabIndex === 1) {
        // Re-run the coverage check with whatever the user last toggled in the Criteri tab. Tabs are
        // not lazy, so this is the only point where the criteria are known to be final.
        if (this.$refs.tabProfileCoverage && this.$refs.comparisonCriteria) {
          this.$refs.tabProfileCoverage.refreshCoverage(this.$refs.comparisonCriteria.getCriteriaPayload())
        }
      }
    },
  },

  created() {
    this.$store.dispatch('efficiencyTestsCommon/newScoringProfile')
    this.loadProductFromQuery()
    // Models are a shared catalog, not EfficiencyTests-specific — see TabScoringDefinition.vue.
    this.$store.dispatch('scoringProfilesModelsList/loadModelsListToAssociate').catch((err) => {
      this.httpErrorsHandler(err)
    })
  },
}
</script>

<style lang="scss" scoped>
.product-test-color {
  color: #212529;
}

.border-rounded {
  border: 1px solid rgb(229, 229, 229);
  border-radius: 8px;
  padding: 22px 22px 26px;
  box-shadow: rgba(0, 0, 0, 0.03) 0px 1px 2px;
  background: rgb(255, 255, 255);
}
</style>
